import { execFile } from "node:child_process";
import { createReadStream, createWriteStream } from "node:fs";
import { mkdtemp, rm, stat, writeFile } from "node:fs/promises";
import { tmpdir } from "node:os";
import path from "node:path";
import { pipeline } from "node:stream/promises";
import { promisify } from "node:util";
import { createGzip } from "node:zlib";
import * as tar from "tar";
import { planSnapshotFiles, type GitSurface } from "./snapshot-artifact.js";

/**
 * In-archive manifest binding a snapshot to the HEAD it was captured on and
 * carrying the deletion list. Lives ONLY inside the tar (assembled in a temp
 * staging dir) — never written into the workspace tree.
 */
export const SNAPSHOT_MANIFEST_NAME = ".conveyor-snapshot-manifest.json";

/**
 * Legacy in-archive name for the postgres dump that pre-#2623 agents bundled
 * into the snapshot tar. Sidecar state is no longer durable, so we neither
 * write nor restore this anymore — but a snapshot captured by an OLDER agent
 * (still sitting on the fixed per-workspace GCS object until the next finalize)
 * can carry one. Extraction must keep SKIPPING this name so the stale dump can
 * never land in the worktree and ride the wip stash's `git add -A` to GitHub.
 */
const LEGACY_PG_DUMP_FILENAME = ".conveyor-pgdump.sql";

export interface SnapshotManifest {
  /** `git rev-parse HEAD` at capture time — restore refuses to extract over a
   *  different HEAD (a stale delta over a newer clone clobbers newer work). */
  head: string;
  /** Repo-relative paths restore must delete after extraction. */
  deletions: string[];
  /** Epoch ms when the tar was BUILT (not uploaded) — also sent as the
   *  `x-snapshot-captured-at` header so the API can refuse out-of-order PUTs. */
  capturedAt: number;
}

// git status --porcelain=v1 -z default maxBuffer (1MB) is too small for large
// dirty trees; 64MB of NUL-delimited paths covers any realistic worktree.
const STATUS_MAX_BUFFER = 64 * 1024 * 1024;

/**
 * Parse `git status --porcelain=v1 -z -uall` output. NUL-delimited and
 * unquoted, so unicode/space/quote filenames arrive verbatim (the LF-based
 * parse broke them via C-style quoting). Rename/copy entries deliver TWO
 * NUL-separated fields — `XY <new>\0<orig>\0`; the NEW path is included and,
 * for renames, the ORIGINAL path joins the deletion list (it exists in HEAD
 * but no longer on disk).
 */
export function parseStatusPorcelainZ(raw: string): { includes: string[]; deletions: string[] } {
  const includes: string[] = [];
  const deletions: string[] = [];
  const tokens = raw.split("\0");
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (!token || token.length < 4) continue;
    const x = token[0];
    const y = token[1];
    const filePath = token.slice(3);
    if (x === "R" || x === "C") {
      const origPath = tokens[++i];
      if (x === "R" && origPath) deletions.push(origPath);
    }
    // Ignored entries (only present with --ignored) never enter the plan.
    if (x === "!") continue;
    // The file is absent from the worktree when the worktree (Y) column is D
    // (covers ` D`, `AD`, `MD`, `RD`, `DD`), or on a staged-only deletion
    // (`D ` — deleted from index, gone from disk, worktree column blank).
    const missingFromWorktree = y === "D" || (x === "D" && y === " ");
    if (missingFromWorktree) deletions.push(filePath);
    else includes.push(filePath);
  }
  return { includes, deletions };
}

// Async git (execFile, no shell): a synchronous status/rev-parse here would
// freeze the agent's event loop for the duration — on an IO-starved pod (the
// exact moment snapshots run) that starves the socket heartbeat and gets the
// session restarted. See runner/git-utils.ts for the full rationale.
const execFileAsync = promisify(execFile);
const GIT_TIMEOUT_MS = 120_000;

/** Single `git status --porcelain=v1 -z -uall` read feeding both GitSurface
 *  views — includes and deletions must come from one consistent status read.
 *  Loaded up-front (async); the returned surface serves it synchronously. */
async function realGitSurface(cwd: string): Promise<GitSurface> {
  const { stdout } = await execFileAsync("git", ["status", "--porcelain=v1", "-z", "-uall"], {
    cwd,
    timeout: GIT_TIMEOUT_MS,
    maxBuffer: STATUS_MAX_BUFFER,
  });
  const cached = parseStatusPorcelainZ(stdout.toString());
  return {
    trackedAndUntracked: () => cached.includes,
    deletedSinceHead: () => cached.deletions,
  };
}

async function gitHead(cwd: string): Promise<string | null> {
  try {
    const { stdout } = await execFileAsync("git", ["rev-parse", "HEAD"], {
      cwd,
      timeout: GIT_TIMEOUT_MS,
    });
    return stdout.toString().trim() || null;
  } catch {
    return null;
  }
}

export interface SnapshotTarResult {
  /** Gzipped tar on disk inside a private temp staging dir. Callers stream it
   *  (never buffer the whole archive) and MUST call cleanup() when done. */
  tarPath: string;
  sizeBytes: number;
  fileCount: number;
  deletionCount: number;
  /** Epoch ms the tar was built — the capture generation marker. */
  capturedAt: number;
  cleanup(): Promise<void>;
}

/**
 * Assemble the snapshot archive: the manifest staged in a temp dir, worktree
 * files appended from the repo. The archive is written to disk and returned as
 * a path so upload can stream it — the tar is never held in memory (pods have
 * an OOM history; a single whole-tar Buffer was up to 500MB).
 */
export async function buildSnapshotTar(cwd: string): Promise<SnapshotTarResult> {
  const staging = await mkdtemp(path.join(tmpdir(), "conveyor-snapshot-"));
  const cleanup = async (): Promise<void> => {
    await rm(staging, { recursive: true, force: true }).catch(() => {});
  };

  try {
    const plan = planSnapshotFiles(await realGitSurface(cwd));
    const head = await gitHead(cwd);
    if (!head) throw new Error("cannot snapshot a repo without a resolvable HEAD");
    const capturedAt = Date.now();

    const manifest: SnapshotManifest = {
      head,
      deletions: plan.deletions,
      capturedAt,
    };
    await writeFile(path.join(staging, SNAPSHOT_MANIFEST_NAME), JSON.stringify(manifest), "utf8");

    const stagedEntries = [SNAPSHOT_MANIFEST_NAME];

    // tar.replace can't operate on a compressed archive, so: create the
    // uncompressed tar from the staging entries, append the repo files with
    // cwd = the repo, then gzip file-to-file (all streaming).
    const rawTarPath = path.join(staging, "snapshot.tar");
    await tar.create({ cwd: staging, file: rawTarPath, portable: true }, stagedEntries);
    if (plan.include.length > 0) {
      await tar.replace({ file: rawTarPath, cwd, portable: true }, plan.include);
    }

    const tarPath = path.join(staging, "snapshot.tar.gz");
    await pipeline(createReadStream(rawTarPath), createGzip(), createWriteStream(tarPath));
    await rm(rawTarPath, { force: true });

    const { size } = await stat(tarPath);
    return {
      tarPath,
      sizeBytes: size,
      fileCount: plan.include.length,
      deletionCount: plan.deletions.length,
      capturedAt,
      cleanup,
    };
  } catch (err) {
    await cleanup();
    throw err;
  }
}

export type SnapshotExtractResult =
  | {
      status: "extracted";
      filesExtracted: number;
      deletionsApplied: number;
    }
  /** Manifest HEAD differs from the current clone's HEAD — extracting would
   *  smear an old delta over newer commits. The tree is NOT touched. */
  | { status: "stale-head"; snapshotHead: string; currentHead: string | null }
  /** Missing/unreadable manifest — not a trustworthy snapshot. Tree untouched. */
  | { status: "invalid" };

/** Repo-relative deletion entries must resolve inside the workspace — a
 *  malicious/corrupt manifest must never delete outside cwd. */
function isWithinWorkspace(cwd: string, rel: string): boolean {
  if (!rel || path.isAbsolute(rel)) return false;
  const root = path.resolve(cwd);
  const abs = path.resolve(root, rel);
  return abs !== root && abs.startsWith(root + path.sep);
}

/**
 * Single `tar.list` pass over the archive to parse the manifest. Returns the
 * validated manifest, or null when it is missing/unreadable/lacks a HEAD.
 */
async function readSnapshotArchive(tarPath: string): Promise<SnapshotManifest | null> {
  let manifestRaw: Buffer | null = null;

  try {
    await tar.list({
      file: tarPath,
      onReadEntry: (entry) => {
        if (entry.path === SNAPSHOT_MANIFEST_NAME) {
          const chunks: Buffer[] = [];
          entry.on("data", (chunk: Buffer) => chunks.push(chunk));
          entry.on("end", () => {
            manifestRaw = Buffer.concat(chunks);
          });
        }
      },
    });
  } catch {
    return null;
  }

  if (!manifestRaw) return null;
  try {
    const parsed = JSON.parse((manifestRaw as Buffer).toString("utf8")) as SnapshotManifest;
    if (typeof parsed.head !== "string" || !parsed.head) return null;
    return {
      head: parsed.head,
      deletions: Array.isArray(parsed.deletions) ? parsed.deletions : [],
      capturedAt: typeof parsed.capturedAt === "number" ? parsed.capturedAt : 0,
    };
  } catch {
    return null;
  }
}

/**
 * Extract a snapshot archive (path to a .tar.gz on disk) into the workspace.
 * Order matters: the manifest is read and the HEAD binding checked BEFORE any
 * tree mutation — a stale or invalid snapshot leaves the tree byte-identical.
 * A valid snapshot with zero files/deletions is still a SUCCESS (a clean tree
 * at capture time is a legitimate snapshot, not a failure).
 */
export async function extractSnapshotTar(
  cwd: string,
  tarPath: string,
): Promise<SnapshotExtractResult> {
  const manifest = await readSnapshotArchive(tarPath);
  if (!manifest) return { status: "invalid" };

  const currentHead = await gitHead(cwd);
  if (manifest.head !== currentHead) {
    return { status: "stale-head", snapshotHead: manifest.head, currentHead };
  }

  let filesExtracted = 0;
  await tar.extract({
    file: tarPath,
    cwd,
    filter: (entryPath) => {
      // Manifest is archive metadata and never lands in cwd. A legacy pg dump
      // (bundled by pre-#2623 agents) is likewise skipped — sidecar state is no
      // longer restored, and a dump in the worktree would ride the next wip
      // stash's `git add -A` to GitHub.
      if (entryPath === SNAPSHOT_MANIFEST_NAME || entryPath === LEGACY_PG_DUMP_FILENAME) {
        return false;
      }
      filesExtracted++;
      return true;
    },
  });

  let deletionsApplied = 0;
  for (const rel of manifest.deletions) {
    if (typeof rel !== "string" || !isWithinWorkspace(cwd, rel)) continue;
    await rm(path.resolve(cwd, rel), { recursive: true, force: true });
    deletionsApplied++;
  }

  return { status: "extracted", filesExtracted, deletionsApplied };
}

/** Test seam: read a manifest back out of an archive without extracting. */
export function readSnapshotManifest(tarPath: string): Promise<SnapshotManifest | null> {
  return readSnapshotArchive(tarPath);
}

/** Test seam: list entry paths in an archive without extracting. */
export async function listSnapshotEntries(tarPath: string): Promise<string[]> {
  const entries: string[] = [];
  await tar.list({
    file: tarPath,
    onReadEntry: (entry) => {
      entries.push(String(entry.path));
    },
  });
  return entries;
}
